import cls from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPreviewTone, updateVoiceInfo, useAppDispatch, useAppSelector } from '../../state';
import { VoiceValue } from './VoiceValue';
import editIcon from '../../assets/Edit/edit.svg';
import deleteIcon from '../../assets/Edit/delete.svg';
import dynamic from 'next/dynamic';
import { LocalImage } from '../Image';
import { useTranslation } from 'next-i18next';
import styles from './voice.module.scss';
import { uploadVoiceIcon } from '../../assets';
import { trackWithNpc } from '../Home';

const VoiceEditModal = dynamic(() =>
  import('./VoiceModal').then(({ VoiceEditModal }) => VoiceEditModal)
);

const PAGE = 'create_npc_advanced_page';

export interface IVoiceEditEntryProps {
  /**
   * quickcreate-async B 方案 (2026-05-25): 是否正在 polling chat-agent 异步任务.
   * caller (apps/xingye /create/edit) 传 useQuickCreatePolling().status !== 'done'.
   * 为 true 且无 npc_tone 时显示 "音色生成中..." + disabled 替代 uploadVoiceIcon.
   */
  isGenerating?: boolean;
  /** 占位文案 — 跟应用 i18n 对齐 */
  generatingText?: string;
}

export function VoiceEditEntry(props: IVoiceEditEntryProps) {
  const dispatch = useAppDispatch();
  const { voiceUrl, voiceInfo, npcId, statusInfo } = useAppSelector((state) => state.edit);
  const { npc_tone } = voiceInfo;
  const { t } = useTranslation();

  const [audioUrl, setAudioUrl] = useState<string>('');

  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!voiceUrl) {
      dispatch(getPreviewTone({ prompt: t('default_voice_prompt') }));
    }
  }, []);

  const hasValue = useMemo(() => !!npc_tone && !!Object.keys(npc_tone).length, [npc_tone]);
  const [showModal, setShowModal] = useState<boolean>(false);
  /** quickcreate-async B 方案: 正在生成 + 无音色 → 走 "音色生成中..." 占位 */
  const isGeneratingPlaceholder = useMemo(
    () => !!props.isGenerating && !hasValue,
    [props.isGenerating, hasValue]
  );

  const onClick = useCallback(() => {
    if (!hasValue) {
      setShowModal(true);
      trackWithNpc(
        { npc_id: npcId, status_info: statusInfo },
        'voice_edit_click',
        PAGE,
        {
          voice_edit_click_type: 'upload',
        },
        'collect'
      );
    }
  }, [hasValue, npcId, statusInfo]);

  const onEdit = useCallback(() => {
    setShowModal(true);
    trackWithNpc(
      { npc_id: npcId, status_info: statusInfo },
      'voice_edit_click',
      PAGE,
      {
        voice_edit_click_type: 'edit',
      },
      'collect'
    );
  }, [npcId, statusInfo]);

  const onDelete = useCallback(() => {
    dispatch(updateVoiceInfo({}));
    trackWithNpc(
      { npc_id: npcId, status_info: statusInfo },
      'voice_edit_click',
      PAGE,
      {
        voice_edit_click_type: 'delete',
      },
      'collect'
    );
  }, [dispatch, npcId, statusInfo]);

  const handleClickAudio = useCallback(() => {
    if (audioRef.current) {
      if (audioUrl) {
        audioRef.current.pause();
        setAudioUrl('');
      } else if (voiceUrl) {
        audioRef.current.src = voiceUrl;
        audioRef.current.play();
        setAudioUrl(voiceUrl);
      }
    }
  }, [audioRef, audioUrl, setAudioUrl, voiceUrl]);

  return (
    <>
      <div
        className={cls(styles.wrapper, {
          [styles.hasValue]: hasValue,
          [styles.isGenerating]: isGeneratingPlaceholder,
        })}
        onClick={isGeneratingPlaceholder ? undefined : onClick}
      >
        {hasValue ? (
          <VoiceValue
            {...props}
            isPlay={!!audioUrl}
            handleVoiceClick={(url) => {
              handleClickAudio();
            }}
            onClick={() => {}}
            voiceUrl={audioUrl}
            voiceName={''}
            tagList={[]}
            buttons={
              <>
                <div className={styles.voiceBtn} onClick={onEdit}>
                  <LocalImage src={editIcon} />
                </div>
                <div className={styles.voiceBtn}>
                  <LocalImage src={deleteIcon} onClick={onDelete} />
                </div>
              </>
            }
          />
        ) : isGeneratingPlaceholder ? (
          <div className={styles.generatingRow}>
            <span className={styles.generatingTag}>
              {props.generatingText || '音色生成中...'}
            </span>
          </div>
        ) : (
          <LocalImage className={styles.uploadIcon} src={uploadVoiceIcon} />
        )}
      </div>
      <VoiceEditModal {...props} show={showModal} setShow={setShowModal} />
      <audio
        ref={audioRef}
        style={{ position: 'absolute', top: -1000, left: -1000 }}
        onEnded={() => {
          setAudioUrl('');
        }}
        preload="auto"
      />
    </>
  );
}
